import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getInitiative } from '../../../../api/initiative-client/initiative-client';
import { fetchPages } from '../../../../api/pages-client/pages-client';
import { searchTasks } from '../../../../api/tasks-client/tasks-client';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { cachedDetails, previewDescription, sameDestination, taskDetails } from './agent-link-preview.helpers';
import { PreviewKind, PreviewSource } from './agent-link-preview.type';
import type {
  CachedDetails,
  LinkPreviewDetailsResult,
  PreviewTarget,
  PreviewTaskRecord,
} from './agent-link-preview.type';

/**
 * Resolves preview details for a link target while its preview is open.
 *
 * Responsibilities:
 * 1. Look up the target by source — task search for Jira/ADO, the initiative
 *    endpoint, or the portfolio's dashboard pages
 * 2. Fall back to task, initiative, and page lists already in the query cache
 *    for the current organization while that lookup is loading, failed, or unavailable
 * 3. Treat a completed lookup as authoritative, so stale cached details never
 *    outlive a fresh result
 *
 * @param {PreviewTarget} target - The recognised link being previewed
 * @param {boolean} opened - Whether the preview is open; nothing is fetched or read until it is
 * @returns {LinkPreviewDetailsResult} The details to render and whether a lookup is in flight
 */
function useLinkPreviewDetails(target: PreviewTarget, opened: boolean): LinkPreviewDetailsResult {
  const queryClient = useQueryClient();
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const canLookUp =
    target.source !== PreviewSource.BLOOMFILTER ||
    (target.kind === PreviewKind.INITIATIVE && !!target.portfolioId && !!target.id) ||
    (target.kind === PreviewKind.DASHBOARD && !!portfolioId && !!target.id);
  const detailsQuery = useQuery<CachedDetails>({
    queryKey: [
      'flora-link-preview',
      organizationId,
      target.source,
      target.url.origin,
      target.url.pathname.replace(/\/$/, ''),
      target.kind,
      target.id,
      target.portfolioId,
      portfolioId,
    ],
    queryFn: async () => {
      if (!organizationId) return {};
      if (target.source !== PreviewSource.BLOOMFILTER && target.id) {
        const tasks = await searchTasks(target.id, organizationId);
        const task = tasks.find((item) => sameDestination(item.url, target.url));
        return task ? taskDetails(task as PreviewTaskRecord, target) : {};
      }
      if (target.kind === PreviewKind.INITIATIVE && target.portfolioId && target.id) {
        const initiative = await getInitiative(target.portfolioId, target.id);
        return { title: initiative.name, description: previewDescription(initiative.description), matched: true };
      }
      if (target.kind === PreviewKind.DASHBOARD && portfolioId && target.id) {
        const pages = await fetchPages(organizationId, portfolioId, 'dashboard', 'dashboard');
        const page = pages.find((item) => item.id === target.id);
        return page ? { title: page.name, description: previewDescription(page.description), matched: true } : {};
      }
      return {};
    },
    enabled: opened && !!organizationId && canLookUp,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });
  // A completed lookup is authoritative, even when it found nothing: the item
  // may have been deleted or become inaccessible since it was cached. Cached
  // details only fill in while the lookup is loading, failed, or disabled.
  let details: CachedDetails = {};
  if (opened)
    details = detailsQuery.isSuccess
      ? detailsQuery.data
      : cachedDetails(queryClient, target, organizationId, portfolioId);
  return { details, isFetching: detailsQuery.isFetching };
}

export { useLinkPreviewDetails };
